import React, { useCallback, useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import BetaBadge from '../components/BetaBadge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Sparkles, RefreshCw, Loader2, Wallet, TrendingUp, Landmark, Percent,
  Hourglass, Repeat, AlertTriangle, PiggyBank, Trophy, Lightbulb, ArrowUpRight, ArrowDownRight,
  Settings, LineChart, Activity,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getMeta, getDashboard, getInsights, getForecast, CUR } from '../components/cfo/api';
import { HealthGauge, MoneyFlow, CategoryBreakdown, CashForecastChart } from '../components/cfo/CfoCharts';
import { CountUp } from '../components/cfo/useCountUp';
import { AddTransactionDialog } from '../components/cfo/AddTransactionDialog';
import { AskCfo } from '../components/cfo/AskCfo';
import { CfoSettingsDialog } from '../components/cfo/CfoSettingsDialog';
import { ExpenseOptimiser } from '../components/cfo/ExpenseOptimiser';
import '../components/cfo/cfo.css';

const PERIODS = [
  ['this_month', 'This month'], ['last_month', 'Last month'], ['3m', 'Last 3 months'],
  ['6m', 'Last 6 months'], ['12m', 'Last 12 months'], ['fy', 'Financial year'],
];

const Card = ({ title, sub, children, className, testid, action, delay = 0 }) => (
  <section className={cn('cfo-card cfo-rise p-5', className)} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    {title && (
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[13px] font-extrabold tracking-wide uppercase text-slate-700">{title}</h3>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5 font-medium">{sub}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

const Kpi = ({ icon: Icon, label, value, render, tone = 'text-slate-900', accent = 'text-cyan-600 bg-cyan-50', hint, testid, delay = 0 }) => (
  <div className="cfo-card cfo-tile cfo-rise px-4 py-3.5 flex flex-col justify-between" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <span className={cn('p-1.5 rounded-lg border border-slate-100 shrink-0', accent)}><Icon className="w-3.5 h-3.5" strokeWidth={2.2} /></span>
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</span>
    </div>
    <div className={cn('font-fin text-2xl font-bold tracking-tight leading-none', tone)}><CountUp value={value} render={render} /></div>
    {hint && <div className="text-[11px] text-slate-400 mt-1.5 font-medium">{hint}</div>}
  </div>
);

const INSIGHT_STYLE = {
  alert: { icon: AlertTriangle, ring: 'border-rose-200 bg-rose-50/60', chip: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
  saving: { icon: PiggyBank, ring: 'border-lime-200 bg-lime-50/60', chip: 'bg-lime-100 text-lime-700', dot: 'bg-lime-500' },
  opportunity: { icon: Lightbulb, ring: 'border-amber-200 bg-amber-50/60', chip: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  win: { icon: Trophy, ring: 'border-emerald-200 bg-emerald-50/60', chip: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

const DeltaPill = ({ v, invert }) => {
  if (v == null) return null;
  const good = invert ? v <= 0 : v >= 0;
  const Icon = v >= 0 ? ArrowUpRight : ArrowDownRight;
  return <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-bold font-fin', good ? 'text-lime-600' : 'text-rose-600')}><Icon className="w-3 h-3" />{Math.abs(v)}%</span>;
};

const CFOContent = () => {
  const { token } = useAuth();
  const [meta, setMeta] = useState(null);
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [ver, setVer] = useState(0);
  const [inclSettle, setInclSettle] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const [d, f] = await Promise.all([getDashboard(token, period), getForecast(token)]);
      setData(d); setForecast(f);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token, period]);

  const loadInsights = useCallback(async (refresh = false) => {
    if (!token) return;
    setInsightsLoading(true);
    try { const r = await getInsights(token, refresh); setInsights(r.cards || []); } catch (e) { console.error(e); } finally { setInsightsLoading(false); }
  }, [token]);

  const reloadMeta = useCallback(() => { if (token) getMeta(token).then(setMeta).catch(console.error); }, [token]);
  useEffect(() => { reloadMeta(); }, [reloadMeta]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (token) loadInsights(false); }, [token, loadInsights]);

  const cur = data?.currency || 'AUD';
  const k = data?.kpis;
  const onSaved = () => { load(true); loadInsights(true); reloadMeta(); setVer((v) => v + 1); };

  return (
    <div className="cfo-scope min-h-screen pb-28 md:pb-10">
      <Header />
      <main className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Title + controls */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-cyan-600 mb-1">
              <Activity className="w-3.5 h-3.5 cfo-pulse" /> Financial Command Centre
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">Chief Financial Officer <BetaBadge /></h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Your money, in plain English — what's happening and what to do about it</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[150px] bg-white border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-cyan-500/30 font-fin text-xs" data-testid="cfo-period"><SelectValue /></SelectTrigger>
              <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => { load(); }} data-testid="cfo-refresh" className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"><RefreshCw className="w-4 h-4" /></Button>
            {meta?.can_manage && <Button variant="ghost" size="icon" onClick={() => setShowSettings(true)} data-testid="cfo-settings-btn" className="text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Settings className="w-4 h-4" /></Button>}
            <Button variant="ghost" size="sm" onClick={() => setShowChat(true)} data-testid="cfo-ask-btn" className="bg-white border border-slate-200 text-cyan-700 hover:bg-cyan-50 hover:text-cyan-800 shadow-sm"><Sparkles className="w-4 h-4 mr-1.5" /> Ask CFO</Button>
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="cfo-add-btn" className="rounded-full bg-slate-900 text-white font-bold hover:bg-slate-800 active:scale-95 transition-transform"><Plus className="w-4 h-4 mr-1.5" /> Add Transaction</Button>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>
        ) : (
          <div className="space-y-5">
            {/* Health + KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card title="Financial Health" sub={`Period: ${data.period.label}`} testid="cfo-health-card" delay={0}>
                <HealthGauge score={data.health.score} label={data.health.label} delta={data.health.delta} />
                <div className="mt-3 text-center text-xs text-slate-500 font-fin">
                  {data.health.runway_months != null ? <>Cash runway <b className="text-slate-800">{data.health.runway_months} months</b></> : 'Add expenses to see cash runway'}
                </div>
              </Card>
              <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3 content-start">
                <Kpi icon={Wallet} label="Cash position" value={k.cash_position} render={(n) => CUR(n, cur)} tone="text-cyan-700" accent="text-cyan-600 bg-cyan-50" hint="Opening + in − out" testid="kpi-cash" delay={40} />
                <Kpi icon={TrendingUp} label="Revenue" value={k.revenue} render={(n) => CUR(n, cur)} tone="text-lime-700" accent="text-lime-600 bg-lime-50" hint={<span>vs prev <DeltaPill v={data.compare.deltas.revenue} /></span>} testid="kpi-revenue" delay={80} />
                <Kpi icon={Landmark} label="Net profit" value={k.net_profit} render={(n) => CUR(n, cur)} tone={k.net_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'} accent="text-emerald-600 bg-emerald-50" hint={<span>vs prev <DeltaPill v={data.compare.deltas.net_profit} /></span>} testid="kpi-profit" delay={120} />
                <Kpi icon={Percent} label="Profit margin" value={k.profit_margin} render={(n) => `${n.toFixed(1)}%`} tone="text-slate-900" accent="text-cyan-600 bg-cyan-50" testid="kpi-margin" delay={160} />
                <Kpi icon={Hourglass} label="Monthly burn" value={k.monthly_burn} render={(n) => CUR(n, cur)} tone="text-slate-900" accent="text-amber-600 bg-amber-50" hint="Avg monthly spend" testid="kpi-burn" delay={200} />
                <Kpi icon={Repeat} label="Recurring / mo" value={k.recurring_monthly} render={(n) => CUR(n, cur)} tone="text-slate-900" accent="text-violet-600 bg-violet-50" hint="Subscriptions & regulars" testid="kpi-recurring" delay={240} />
                <Kpi icon={Landmark} label="Expenses" value={k.expenses} render={(n) => CUR(n, cur)} tone="text-rose-700" accent="text-rose-600 bg-rose-50" hint={<span>vs prev <DeltaPill v={data.compare.deltas.expenses} invert /></span>} testid="kpi-expenses" delay={280} />
                <Kpi icon={Percent} label="GST estimate" value={k.gst_estimate} render={(n) => CUR(n, cur)} tone="text-slate-900" accent="text-cyan-600 bg-cyan-50" hint="Collected − paid" testid="kpi-gst" delay={320} />
                <Kpi icon={PiggyBank} label="Tax reserve" value={k.tax_reserve} render={(n) => CUR(n, cur)} tone="text-slate-900" accent="text-lime-600 bg-lime-50" hint="Suggested set-aside" testid="kpi-tax" delay={360} />
              </div>
            </div>

            {/* Money in vs out */}
            <Card title="Money In vs Money Out" sub="Last 12 months" testid="cfo-flow-card" delay={80}>
              <MoneyFlow data={data.money_flow} currency={cur} />
            </Card>

            {/* Cash flow forecast */}
            {forecast && (() => {
              const hasSettle = !!forecast.blended;
              const useBlend = hasSettle && inclSettle;
              const horizons = useBlend ? forecast.blended.horizons : forecast.horizons;
              const series = useBlend ? forecast.blended.series : forecast.series;
              const sub = forecast.has_data
                ? `Based on ~${CUR(forecast.assumptions.monthly_income, cur)}/mo in · ~${CUR(forecast.assumptions.monthly_expense, cur)}/mo out · net ${CUR(forecast.assumptions.monthly_net, cur)}/mo`
                : (hasSettle ? `Projected from Derwent settlement commission · ${CUR(forecast.blended.monthly_commission, cur)}/mo expected in` : 'Add income & expenses to project your cash');
              return (
              <Card title="Cash Flow Forecast" sub={sub} testid="cfo-forecast-card" delay={120}
                action={<span className="inline-flex items-center gap-1 text-[11px] text-cyan-600 font-fin uppercase tracking-wider"><LineChart className="w-3.5 h-3.5" /> Projected</span>}>
                {hasSettle && (
                  <button type="button" onClick={() => setInclSettle((v) => !v)} data-testid="cfo-include-settlements"
                    className={cn('mb-4 w-full flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors', useBlend ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100')}>
                    <span className={cn('relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors', useBlend ? 'bg-emerald-500' : 'bg-slate-300')}>
                      <span className={cn('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', useBlend ? 'translate-x-4' : 'translate-x-0.5')} />
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                      <Landmark className="w-3.5 h-3.5 text-emerald-600" /> Include Derwent settlement income
                      <span className="font-fin font-bold text-emerald-700">+{CUR(forecast.blended.monthly_commission, cur)}/mo</span>
                    </span>
                  </button>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-4">
                  {horizons.map((h) => (
                    <div key={h.key} className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5" data-testid={`forecast-${h.key}`}>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{h.label}</div>
                      <div className="font-fin text-lg font-bold text-slate-900 leading-tight mt-0.5">{CUR(h.projected_cash, cur)}</div>
                      <div className={cn('text-[11px] font-bold font-fin', h.change >= 0 ? 'text-lime-600' : 'text-rose-600')}>{h.change >= 0 ? '+' : ''}{CUR(h.change, cur)}</div>
                    </div>
                  ))}
                </div>
                <CashForecastChart series={series} currency={cur} />
                {hasSettle && (
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500" data-testid="cfo-settlement-source">
                    <PiggyBank className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    Settlement income is live from the CEO dashboard — {CUR(forecast.settlement_income.projected_settled, cur)} projected settlements this month × {(forecast.settlement_income.effective_commission * 100).toFixed(4)}% effective commission.
                  </div>
                )}
                {forecast.runway_months != null && (
                  <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="forecast-runway-warning">
                    <span className="w-2 h-2 rounded-full bg-rose-500 cfo-pulse shrink-0" /> At this burn rate your cash runs out in about <b className="font-fin">{forecast.runway_months} months</b>{forecast.runs_out_on ? ` (around ${new Date(forecast.runs_out_on).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })})` : ''}.
                  </div>
                )}
              </Card>
              );
            })()}

            {/* Where money went + recurring */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card title="Where your money went" sub={data.period.label} testid="cfo-where-card" delay={140}>
                <CategoryBreakdown rows={data.expense_breakdown} currency={cur} />
              </Card>
              <Card title="Recurring commitments" sub="Auto-detected subscriptions & regular payments" testid="cfo-recurring-card" delay={160}
                action={<div className="text-right"><div className="font-fin text-xl font-bold text-slate-900 leading-none">{CUR(data.recurring.monthly_total, cur)}</div><div className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">per month</div></div>}>
                {data.recurring.items.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No recurring payments detected yet.</div> : (
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {data.recurring.items.map((r) => (
                      <div key={r.merchant} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`recurring-${r.merchant}`}>
                        <div className="min-w-0"><div className="text-sm font-semibold text-slate-800 truncate">{r.merchant}</div><div className="text-[11px] text-slate-400">{r.category || '—'} · {r.occurrences}×</div></div>
                        <div className="text-right shrink-0"><div className="font-fin text-sm font-bold text-slate-900">{CUR(r.monthly, cur)}</div>{r.increased && <div className="text-[10px] font-bold text-rose-600 font-fin">▲ {r.increase_pct}%</div>}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* AI insights */}
            <section data-testid="cfo-insights" className="cfo-rise" style={{ animationDelay: '180ms' }}>
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="text-[13px] font-extrabold uppercase tracking-wide text-slate-700 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-lime-600" /> DerwentAI CFO Insights</h3><p className="text-[11px] text-slate-400">What to watch, what to cut, what you're doing well</p></div>
                <button onClick={() => loadInsights(true)} disabled={insightsLoading} className="text-xs font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="refresh-insights">
                  {insightsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
                </button>
              </div>
              {insightsLoading && !insights ? <div className="h-24 flex items-center justify-center text-cyan-600"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(insights || []).map((c, i) => {
                    const st = INSIGHT_STYLE[c.type] || INSIGHT_STYLE.opportunity;
                    const Icon = st.icon;
                    return (
                      <div key={i} className={cn('rounded-xl border p-4', st.ring)} data-testid={`insight-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className={cn('inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full', st.chip)}><span className={cn('w-1.5 h-1.5 rounded-full', st.dot)} /> {c.type}</span>
                          {c.value && <span className="font-fin text-sm font-bold text-slate-900">{c.value}</span>}
                        </div>
                        <div className="text-sm font-extrabold text-slate-900 leading-tight">{c.title}</div>
                        <div className="text-xs text-slate-500 mt-1 leading-relaxed">{c.body}</div>
                      </div>
                    );
                  })}
                  {(!insights || insights.length === 0) && !insightsLoading && (
                    <div className="col-span-full cfo-card px-6 py-8 text-center text-sm text-slate-400">Add a few transactions and I'll start spotting patterns for you.</div>
                  )}
                </div>
              )}
            </section>

            {/* Expense Optimiser */}
            <ExpenseOptimiser token={token} currency={cur} reloadKey={ver} />
          </div>
        )}
      </main>

      {/* Floating Ask CFO (bottom-left to avoid the global Hey Derwent launcher) */}
      <button onClick={() => setShowChat(true)} data-testid="cfo-fab"
        className="fixed bottom-24 md:bottom-6 left-4 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-slate-900 text-white shadow-lg hover:scale-105 transition-transform">
        <Sparkles className="w-4 h-4 text-lime-400" /> <span className="font-bold text-sm">Ask CFO</span>
      </button>

      <AddTransactionDialog open={showAdd} onOpenChange={setShowAdd} meta={meta} token={token} onSaved={onSaved} />
      <AskCfo open={showChat} onOpenChange={setShowChat} token={token} />
      <CfoSettingsDialog open={showSettings} onOpenChange={setShowSettings} meta={meta} token={token} onSaved={onSaved} />
      <Footer />
    </div>
  );
};

const CFO = () => <CFOContent />;
export default CFO;
